import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import mabAdPng from "../../assets/images/png/mab-ad.png";
import mabAd2 from "../../assets/images/svg/mab-ad2-1.svg";

import mabAdArrow from "../../assets/images/svg/mab-ad-arrow.svg";
import stepOneIcon from "../../assets/images/svg/mab-1.svg";
import stepTwoIcon from "../../assets/images/svg/mab2.svg";
import stepTwoActiveIcon from "../../assets/images/svg/mab-ad2.1.svg";
import brandLogo from "../../assets/images/svg/white-logo-new.svg";
import downArrow from "../../assets/images/svg/mab-down-arrow.svg";
import campaignStepOneIcon from "../../assets/images/svg/mab-compgn-1.svg";
import campaignMobileIcon from "../../assets/images/svg/mab-compgn-mobile.svg";
import ellipseBg from "../../assets/images/svg/mab-ellipse.svg";
import lineSvg from "../../assets/images/svg/mab-line-2.svg";
import line1Svg from "../../assets/images/svg/line1svg.svg";
import MobileAdPreview from "./meta-mobile-ad-preview";
import AudioDropdown from "../../components/audio-dropdown";
import VideoDropdown from "../../components/metaBuilder/MetaBuilderVideoDropdown";
import LandingDropdown from "../../components/landing-dropdown";
import InterestsDropdown from "../../components/metaBuilder/MetaBuilderInterestsDropdown";
import BudgetDropdown from "../../components/metaBuilder/MetaBuilderBudgetDropdown";
import AdvancedSettingsDropdown from "../../components/metaBuilder/MetaBuilderAdvancedSettingsDropdown";
import ConfirmationDropdown from "../../components/metaBuilder/MetaBuilderConfirmationDropdown";
import MusicDropdown from "../../components/metaBuilder/MetaBuilderMusicDropdown";
import TrackGenreDropdown from "../../components/track-genre-dropdown";
import CountriesDropdown from "../../components/metaBuilder/MetaBuilderCountriesDropdown";
import {
  useGetArtistListQuery,
  useLazyCheckBannedArtistQuery,
} from "../../components/apis/artistsApi";
import {
  useGetMetaBuilderCampaignFromMetaQuery,
  useGetVideosByIdsQuery,
} from "../../components/apis/metaBuilderApi";
import { ROUTES } from "../../components/consts/routes.consts";
import { apiBaseUrl } from "../../components/consts/api-url.const";
import NextButton from "../../components/common/next-button";

const AdNameStepBar: React.FC<{ showSecondSection: boolean }> = ({
  showSecondSection,
}) => (
  <div className="w-full max-w-[1308px] mx-auto mb-6">
    <div className="flex items-center gap-4 px-6 py-4 bg-[#0F0F11] rounded-[24px]">
      <p className="text-white text-[18px] leading-[130%] tracking-[0] font-medium [font-family:'Poppins',Helvetica]">
        Set up the ad account,
        <br />
        Pixel, and campaign name
      </p>

      <div className="flex items-center gap-2 flex-1">
        <img src={stepOneIcon} alt="Step 1" className="w-[80px] h-[80px]" />
        <div className="flex-1 flex items-center justify-center">
          <img src={showSecondSection ? lineSvg : line1Svg} alt="Line" className="w-full h-[2px]" />
        </div>
        <div className="flex items-center gap-1">
          <img
            src={showSecondSection ? stepTwoActiveIcon : stepTwoIcon}
            alt="Step 2"
            className="w-[80px] h-[80px]"
          />
          <span className="text-white text-[18px] leading-[130%] tracking-[0] font-medium [font-family:'Poppins',Helvetica] text-right whitespace-nowrap">
            Campaign Set Up
          </span>
        </div>
      </div>
    </div>
  </div>
);

const AdNameNavbar: React.FC = () => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    // Clear meta ads selections from sessionStorage
    sessionStorage.removeItem("metaAdsSelections");
    // Clear meta ads account data from localStorage
    localStorage.removeItem("metaAdsAccountData");
    navigate(ROUTES.META_ADS_SETUP_PUBLIC);
  };

  return (
    <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 mb-4">
      <div className="flex items-center justify-between gap-10">
        <div className="flex items-center gap-10">
          <button
            onClick={handleBackClick}
            className="inline-flex items-center gap-[10px] w-[124px] h-[48px] px-[12px] pr-[32px] py-[12px] rounded-[999px] bg-white text-[#0B0B0B] shadow-[0_4px_12px_rgba(0,0,0,0.25)] hover:translate-y-[-1px] transition-transform"
            style={{ opacity: 1 }}
          >
            <img
              src={mabAdArrow}
              alt="Back"
              className="w-[25px] h-[25px] rotate-180"
            />
            <span className="text-[16px] font-semibold [font-family:'Poppins',Helvetica]">
              Back
            </span>
          </button>

          <img src={brandLogo} alt="PitchPlaylists" className="h-9 w-auto" />
        </div>
      </div>

      <div className="mt-3 h-px w-full bg-white/10" />
    </div>
  );
};
// meta new compaing ad name section
const AdNameSection: React.FC<{
  onNext: () => void;
  adName: string;
  onAdNameChange: (v: string) => void;
  isEditMode?: boolean;
}> = ({ onNext, adName, onAdNameChange, isEditMode = false }) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Limit to 35 characters
    if (value.length <= 35 && !isEditMode) {
      onAdNameChange(value);
    }
  };

  // Check if input is empty or contains only whitespace
  const isDisabled = !adName || !adName.trim();

  return (
    <section
      className="relative z-[2] w-full max-w-[1308px] h-[358px] rounded-[40px] bg-center bg-no-repeat p-16 flex flex-col gap-6 opacity-100 mx-auto"
      style={{
        backgroundImage: `url(${mabAdPng})`,
        backgroundSize: "100% 100%",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}
    >
      <div className="flex flex-col gap-6 w-full h-full">
        <h2 className="text-white font-bold text-[32px] leading-[100%] tracking-[0] [font-family:'Poppins',Helvetica]">
          Ad Name
        </h2>

        <div className="flex flex-col gap-3 flex-1 w-full items-start">
          <input
            type="text"
            value={adName}
            onChange={handleInputChange}
            maxLength={35}
            placeholder="Enter the name of your upcoming ad campaign"
            disabled={isEditMode}
            className="w-full max-w-[1180px] h-[74px] rounded-[20px] bg-[#878DA14D] border border-transparent text-white text-base px-6 focus:outline-none focus:ring-2 focus:ring-[#58F2A9] focus:border-transparent shadow-[0_10px_30px_rgba(0,0,0,0.35)] placeholder:text-left placeholder:text-[16px] placeholder:leading-[130%] placeholder:tracking-[0] placeholder:font-[16px] placeholder:text-[#FFFFFF99]"
            style={{
              opacity: isEditMode ? 0.6 : 1,
              cursor: isEditMode ? "not-allowed" : "text",
            }}
          />
        </div>

        <div className="flex w-full justify-end">
          <NextButton size="sm" onClick={onNext} disabled={isDisabled} />
        </div>
      </div>
    </section>
  );
};

export default function CreateCampaign() {
  // Parse campaign ID and draft ID from URL query params for edit mode
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaignId');
  const draftId = searchParams.get('draftId');
  const isEditMode = !!campaignId;
  const isDraftMode = !!draftId;
  const isEditOrDraftMode = isEditMode || isDraftMode;

  // Clear currentDraftId from sessionStorage when creating a new campaign
  useEffect(() => {
    if (!isDraftMode) {
      sessionStorage.removeItem("currentDraftId");
    }
  }, [isDraftMode]);

  // Fetch campaign data for edit mode
  const { data: campaignData, isLoading: isLoadingCampaign } = useGetMetaBuilderCampaignFromMetaQuery(
    { campaignId: campaignId! },
    { skip: !campaignId }
  );

  // State for draft data to pass to dropdowns
  const [draftCountries, setDraftCountries] = useState<string[]>([]);
  const [draftInterests, setDraftInterests] = useState<any[]>([]);
  const [draftTargeting, setDraftTargeting] = useState<{
    age_min?: number;
    age_max?: number;
    genders?: number[];
  }>({});
  const [draftAdText, setDraftAdText] = useState<{
    message?: string;
    headlineTop?: string;
  }>({});

  const [showSecondSection, setShowSecondSection] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [expandedDropdown, setExpandedDropdown] = useState<string | null>(null);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const previewElementRef = useRef<HTMLDivElement>(null);
  const [adName, setAdName] = useState<string>("");
  const [spotifyUrl, setSpotifyUrl] = useState<string>("");
  const [artistName, setArtistName] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [showAdditionalFields, setShowAdditionalFields] = useState<boolean>(false);
  const [audioFileName, setAudioFileName] = useState<string>("");
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [isAudioUploading, setIsAudioUploading] = useState<boolean>(false);
  const [audioUploadProgress, setAudioUploadProgress] = useState<number>(0);
  const [videos, setVideos] = useState<Array<{ id: string; fileName: string; file?: File; isUploading: boolean; uploadProgress: number; facebookVideoId?: string; videoUrl?: string; thumbnailUrl?: string }>>([]);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const videoUploadIntervalsRef = useRef<Record<string, NodeJS.Timeout>>({});
  const [landingFileName, setLandingFileName] = useState<string>("");
  const [landingFile, setLandingFile] = useState<File | null>(null);
  const [isLandingUploading, setIsLandingUploading] = useState<boolean>(false);
  const [landingUploadProgress, setLandingUploadProgress] = useState<number>(0);
  const [isFetchingTrack, setIsFetchingTrack] = useState<boolean>(false);
  const [trackImageUrl, setTrackImageUrl] = useState<string | null>(null);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>("Tier One Countries"); // Default to Tier One Countries
  const [selectedInterests, setSelectedInterests] = useState<Array<{ id: string; name: string; audience_size?: number; path?: string[] }>>([]);
  const [metaAdsSelections, setMetaAdsSelections] = useState<{
    pageName?: string | null;
    adAccountName?: string | null;
    instagramName?: string | null;
    pixelName?: string | null;
    adAccountId?: string | null;
    pageId?: string | null;
    instagramId?: string | null;
    pixelId?: string | null;
  }>({});
  const [audienceAge, setAudienceAge] = useState<string>("All age");
  const [audienceGender, setAudienceGender] = useState<string>("All");
  const [adText, setAdText] = useState<string>("");
  const [adHeadline, setAdHeadline] = useState<string>("Listen now on Spotify!");
  const [campaignBudget, setCampaignBudget] = useState<string>("");
  const [currency, setCurrency] = useState<string>("");

  // Wrapper to log budget changes
  const handleBudgetChange = (newBudget: string) => {
    setCampaignBudget(newBudget);
  };
  const [campaignStartDate, setCampaignStartDate] = useState<string>("None (run when published)");
  const [campaignEndDate, setCampaignEndDate] = useState<string>("None (run until turned off manually)");
  const [isScheduled, setIsScheduled] = useState<"always" | "scheduled">("always");
  const [timezone, setTimezone] = useState<string>("Helsinki, Finland (GMT+2)");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<string>("00:00");
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<string>("00:00");
  const lastProcessedTrackIdRef = useRef<string>("");
  const [previewOffset, setPreviewOffset] = useState<number>(0);

  // Normalize Spotify URL and extract track ID using useMemo to prevent unnecessary recalculations
  const normalizedInput = useMemo(() => {
    return spotifyUrl.replace(/\/intl-[a-z]{2}\//i, "/");
  }, [spotifyUrl]);

  const trackId = useMemo(() => {
    if (!normalizedInput.includes("/track/")) return "";
    const parts = normalizedInput.split("/");
    const lastPart = parts[parts.length - 1];
    const [id] = lastPart.split("?");
    return id;
  }, [normalizedInput]);

  // Set up API query hook
  const { refetch } = useGetArtistListQuery<any>(
    { trackId: trackId },
    {
      skip: !trackId || !normalizedInput.includes("/track/"),
    }
  );
  const [checkBannedArtist] = useLazyCheckBannedArtistQuery();

  const handleDropdownToggle = (id: string) => {
    setExpandedDropdown((prev) => (prev === id ? null : id));
  };

  // Define dropdown order (only include active/visible dropdowns)
  const dropdownOrder = ["music", "video", "countries", "interests", "budget", "advanced", "confirmation"];

  // Handle next button click - close current dropdown and open next one
  const handleNext = (currentId: string) => {
    const currentIndex = dropdownOrder.indexOf(currentId);
    if (currentIndex !== -1 && currentIndex < dropdownOrder.length - 1) {
      const nextId = dropdownOrder[currentIndex + 1];
      setExpandedDropdown(nextId);
      // Scroll to next dropdown after a short delay
      setTimeout(() => {
        const nextElement = dropdownRefs.current[nextId];
        if (nextElement) {
          nextElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }, 100);
    }
  };

  useEffect(() => {
    const header = document.querySelector("header") as HTMLElement | null;
    const footer = document.querySelector(".site-footer") as HTMLElement | null;

    if (header) {
      const prevDisplay = header.style.display;
      header.dataset.prevDisplay = prevDisplay;
      header.style.display = "none";
    }

    if (footer) {
      const prevDisplay = footer.style.display;
      footer.dataset.prevDisplay = prevDisplay;
      footer.style.display = "none";
    }

    return () => {
      if (header) {
        header.style.display = header.dataset.prevDisplay || "";
        delete header.dataset.prevDisplay;
      }
      if (footer) {
        footer.style.display = footer.dataset.prevDisplay || "";
        delete footer.dataset.prevDisplay;
      }
    };
  }, []);

  // Read meta ads selections from sessionStorage
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("metaAdsSelections");
      if (raw) {
        const parsed = JSON.parse(raw);
        const sessionData = {
          pageName: parsed?.page?.name ?? null,
          adAccountName: parsed?.adAccount?.name
            ? parsed.adAccount.id
              ? `${parsed.adAccount.name} [${parsed.adAccount.id}]`
              : parsed.adAccount.name
            : null,
          instagramName: parsed?.instagram?.name ?? null,
          pixelName: parsed?.pixel?.name ?? null,
          adAccountId: parsed?.adAccount?.id ?? null,
          pageId: parsed?.page?.id ?? null,
          instagramId: parsed?.instagram?.id ?? null,
          pixelId: parsed?.pixel?.id ?? null,
        };


        setMetaAdsSelections(sessionData);
      }
    } catch (e) {
      console.error("Failed to read meta ads selections", e);
    }
  }, []);

  // Reset fetching state when URL changes significantly
  useEffect(() => {
    if (!normalizedInput.includes("/track/")) {
      setIsFetchingTrack(false);
      lastProcessedTrackIdRef.current = "";
    }
  }, [normalizedInput]);

  // Auto-fetch track data when Spotify URL is pasted
  useEffect(() => {
    if (!trackId || !normalizedInput.includes("/track/")) return;
    if (isFetchingTrack) return;
    // Prevent duplicate calls for the same trackId
    if (lastProcessedTrackIdRef.current === trackId) return;

    const fetchTimeout = setTimeout(async () => {
      try {
        setIsFetchingTrack(true);
        lastProcessedTrackIdRef.current = trackId;
        const response: any = await refetch({ trackId: trackId });

        if (response.status === "fulfilled" && response.data) {
          const trackData = response.data;

          // Check if any artist is banned
          if (trackData.artists && trackData.artists.length > 0) {
            for (const artist of trackData.artists) {
              if (artist.id) {
                try {
                  const bannedCheck = await checkBannedArtist({ artistId: artist.id }).unwrap();
                  if (bannedCheck.isBanned) {
                    // Clear fields if artist is banned
                    setArtistName("");
                    setTitle("");
                    setTrackImageUrl(null);
                    setIsFetchingTrack(false);
                    return;
                  }
                } catch (err) {
                  console.error("Error checking banned artist:", err);
                }
              }
            }
          }

          // Auto-populate artist name and title
          if (trackData.artists && trackData.artists.length > 0) {
            const artistNames = trackData.artists
              .map((artist: any) => artist.name)
              .filter(Boolean);
            setArtistName(artistNames.join(", ") || "");
          }

          if (trackData.name) {
            setTitle(trackData.name);
          }

          // Set track image URL for preview
          if (trackData.album?.images && trackData.album.images.length > 0) {
            // Use medium size image (index 1) or fallback to first image
            const imageUrl = trackData.album.images[1]?.url || trackData.album.images[0]?.url;
            setTrackImageUrl(imageUrl || null);
          } else {
            setTrackImageUrl(null);
          }
        }
      } catch (error) {
        console.error("Error fetching track:", error);
        // Reset the ref on error so it can be retried
        lastProcessedTrackIdRef.current = "";
      } finally {
        setIsFetchingTrack(false);
      }
    }, 300);

    return () => clearTimeout(fetchTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackId]);

  // Handle Spotify URL input change
  const handleSpotifyUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setSpotifyUrl(url);

    // Show additional fields if URL has any value
    if (url && url.trim().length > 0) {
      setShowAdditionalFields(true);
    } else {
      setShowAdditionalFields(false);
      // Clear additional fields if URL is empty
      setArtistName("");
      setTitle("");
      setTrackImageUrl(null);
    }
  };

  const handleAudioUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setAudioUploadProgress(0);

    // Only process if a file is actually selected
    // Don't clear existing audio if dialog is closed without selection
    if (!file) {
      return;
    }

    setAudioFileName(file.name);
    setAudioFile(file);
    setIsAudioUploading(true);

    let progress = 0;
    const interval = setInterval(() => {
      progress += 15;
      if (progress >= 100) {
        progress = 100;
        setAudioUploadProgress(progress);
        clearInterval(interval);
        setIsAudioUploading(false);
      } else {
        setAudioUploadProgress(progress);
      }
    }, 200);
  };

  const handleRemoveAudioUpload = () => {
    setAudioFileName("");
    setAudioFile(null);
    setIsAudioUploading(false);
    setAudioUploadProgress(0);
  };

  const handleVideoUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    const MAX_VIDEOS = 5;

    // Only process if files are actually selected
    // Don't clear existing videos if dialog is closed without selection
    if (!files || files.length === 0) {
      return;
    }

    // Calculate how many videos can still be added
    const currentCount = videos.length;
    const remainingSlots = MAX_VIDEOS - currentCount;
    const filesToAdd = Array.from(files).slice(0, remainingSlots);

    if (filesToAdd.length === 0) {
      return; // Already at max capacity
    }

    // Add new videos with uploading state
    const newVideos = filesToAdd.map((file) => {
      const videoId = `${Date.now()}-${Math.random()}`;
      return {
        id: videoId,
        fileName: file.name,
        file: file,
        isUploading: true,
        uploadProgress: 0,
      };
    });

    setVideos((prev) => [...prev, ...newVideos]);

    // Simulate upload progress for each new video
    newVideos.forEach((video) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += 15;
        if (progress >= 100) {
          progress = 100;
          setVideos((prev) => {
            const updatedVideos = prev.map((v) =>
              v.id === video.id
                ? { ...v, isUploading: false, uploadProgress: 100 }
                : v
            );

            // Always select the first completed video (index 0) when videos are available
            const completedVideos = updatedVideos.filter(v => !v.isUploading);
            if (completedVideos.length > 0) {
              setSelectedVideoId(completedVideos[0].id);
            }

            return updatedVideos;
          });
          if (videoUploadIntervalsRef.current[video.id]) {
            clearInterval(videoUploadIntervalsRef.current[video.id]);
            delete videoUploadIntervalsRef.current[video.id];
          }
        } else {
          setVideos((prev) =>
            prev.map((v) =>
              v.id === video.id ? { ...v, uploadProgress: progress } : v
            )
          );
        }
      }, 200);
      videoUploadIntervalsRef.current[video.id] = interval;
    });
  };

  const handleRemoveVideo = (videoId: string) => {
    // Clear interval if video is still uploading
    if (videoUploadIntervalsRef.current[videoId]) {
      clearInterval(videoUploadIntervalsRef.current[videoId]);
      delete videoUploadIntervalsRef.current[videoId];
    }
    // Remove video from state
    setVideos((prev) => prev.filter((v) => v.id !== videoId));
  };

  const handleLandingUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setLandingUploadProgress(0);

    // Only process if a file is actually selected
    // Don't clear existing landing image if dialog is closed without selection
    if (!file) {
      return;
    }

    setLandingFileName(file.name);
    setLandingFile(file);
    setIsLandingUploading(true);

    let progress = 0;
    const interval = setInterval(() => {
      progress += 20;
      if (progress >= 100) {
        progress = 100;
        setLandingUploadProgress(progress);
        clearInterval(interval);
        setIsLandingUploading(false);
      } else {
        setLandingUploadProgress(progress);
      }
    }, 200);
  };

  const handleRemoveLandingUpload = () => {
    setLandingFileName("");
    setIsLandingUploading(false);
    setLandingUploadProgress(0);
  };

  // Cleanup upload intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(videoUploadIntervalsRef.current).forEach((interval) => {
        clearInterval(interval);
      });
    };
  }, []);

  // Container-bounded smooth scroll-following effect for preview
  // This effect makes the phone mockup preview smoothly follow scroll while respecting container boundaries
  // - Starts following when container top hits 100px from viewport top
  // - Stops when reaching 40px from bottom of container
  // - Uses smooth easing (0.08 factor) for fluid animation
  useEffect(() => {
    let animationFrameId: number;

    const animate = () => {
      const contentContainer = contentContainerRef.current;
      const previewElement = previewElementRef.current;

      if (!contentContainer || !previewElement) {
        animationFrameId = requestAnimationFrame(animate);
        return;
      }

      // Get container and element dimensions
      const containerRect = contentContainer.getBoundingClientRect();
      const previewHeight = previewElement.offsetHeight;
      const containerHeight = contentContainer.offsetHeight;

      // Calculate scroll position relative to container
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const containerTop = containerRect.top + scrollTop;
      const containerBottom = containerTop + containerHeight;

      // Sticky offset from top of viewport when element becomes sticky
      const stickyTopOffset = 100;
      const minViewportMargin = 20; // Minimum margin from top of viewport

      // Determine where preview should be positioned
      let targetOffset = 0;

      // When container top is below sticky position, element should be at sticky position
      if (containerRect.top > stickyTopOffset) {
        // Container is below sticky position - element follows container
        targetOffset = 0;
      } else {
        // Calculate how far we've scrolled past the sticky point
        const scrollPastSticky = stickyTopOffset - containerRect.top;

        // Calculate the maximum scroll distance before the preview hits the bottom
        // The preview starts 65px down, so available scrollable distance is:
        // containerHeight - 65 (initial offset) - previewHeight - 40 (bottom margin)
        const maxScrollableDistance = containerHeight - 65 - previewHeight - 40;

        // Cap the offset to prevent going beyond the bottom
        let clampedOffset = Math.min(scrollPastSticky, Math.max(0, maxScrollableDistance));

        // Ensure preview doesn't go above viewport - prevent hiding from top
        // Calculate where preview would be in viewport after applying offset
        const previewTopInViewport = containerRect.top + 65 + clampedOffset;
        const minAllowedViewportTop = minViewportMargin;

        // If preview would go above viewport, adjust offset to keep it visible
        if (previewTopInViewport < minAllowedViewportTop) {
          const offsetAdjustment = minAllowedViewportTop - previewTopInViewport;
          clampedOffset = Math.max(0, clampedOffset + offsetAdjustment);
        }

        targetOffset = clampedOffset;
      }

      // Apply smooth easing
      setPreviewOffset((currentOffset) => {
        const diff = targetOffset - currentOffset;
        const easingFactor = 0.08; // Smooth easing
        const newOffset = currentOffset + diff * easingFactor;

        // Stop if very close to target to prevent infinite micro-movements
        if (Math.abs(diff) < 0.5) {
          return targetOffset;
        }

        return newOffset;
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    // Start animation loop
    animationFrameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // Pre-fill form data when editing a campaign
  useEffect(() => {
    if (isEditMode && campaignData) {
      const { campaign, account, countries, linkUrls, adSets, ads } = campaignData;

      // Pre-fill basic fields from campaign
      setAdName(campaign.name || "");

      // Pre-fill budget (daily_budget or lifetime_budget from single campaign endpoint is a string in cents)
      const budgetValue = campaign.daily_budget || campaign.lifetime_budget;
      if (budgetValue) {
        // Handle both string and number types
        const budgetInCents = typeof budgetValue === 'string'
          ? parseFloat(budgetValue)
          : budgetValue;
        const budgetInEuros = budgetInCents / 100;
        setCampaignBudget(budgetInEuros.toString());
      }

      // Pre-fill schedule - check if campaign has stop_time (scheduled) or not (always running)
      if (campaign.stop_time) {
        // Campaign is scheduled with end date
        setIsScheduled('scheduled');
        setStartDate(dayjs(campaign.start_time));
        setEndDate(dayjs(campaign.stop_time));

        // Set times from the start and end time
        const startFormatted = dayjs(campaign.start_time).format('HH:mm');
        const endFormatted = dayjs(campaign.stop_time).format('HH:mm');
        setStartTime(startFormatted);
        setEndTime(endFormatted);
      } else {
        // Campaign is always running (no stop_time)
        setIsScheduled('always');
        setStartDate(dayjs(campaign.start_time));
      }

      // Pre-fill timezone from account
      if (account.timezone_name) {
        // Format timezone for display (e.g., "Europe/Helsinki" -> "Helsinki, Finland (GMT+2)")
        // For now, just use the timezone_name directly or map it
        setTimezone(account.timezone_name);
      }

      // Pre-fill currency from account
      if (account.currency) {
        setCurrency(account.currency);
      }

      // Pre-fill Spotify URL from linkUrls and clear previous track data
      if (linkUrls && linkUrls.length > 0) {
        const url = linkUrls[0];
        setSpotifyUrl(url);
        // Clear previous track data to ensure fresh fetch
        setArtistName("");
        setTitle("");
        setTrackImageUrl(null);

        // Extract track ID from URL and fetch track data immediately
        if (url.includes('/track/')) {
          const parts = url.split('/track/');
          const trackId = parts[1]?.split('?')[0];
          if (trackId) {
            // Reset the ref to allow re-fetching this track in edit mode
            lastProcessedTrackIdRef.current = "";
            setShowAdditionalFields(true);

            // Manually trigger track data fetch for edit mode
            setTimeout(async () => {
              try {
                setIsFetchingTrack(true);
                lastProcessedTrackIdRef.current = trackId;

                const response: any = await refetch({ trackId: trackId });

                if (response.status === "fulfilled" && response.data) {
                  const trackData = response.data;

                  // Check if any artist is banned
                  if (trackData.artists && trackData.artists.length > 0) {
                    for (const artist of trackData.artists) {
                      if (artist.id) {
                        try {
                          const bannedCheck = await checkBannedArtist({ artistId: artist.id }).unwrap();
                          if (bannedCheck.isBanned) {
                            // Clear fields if artist is banned
                            setArtistName("");
                            setTitle("");
                            setTrackImageUrl(null);
                            setIsFetchingTrack(false);
                            return;
                          }
                        } catch (err) {
                          console.error("Error checking banned artist:", err);
                        }
                      }
                    }
                  }

                  // Auto-populate artist name and title
                  if (trackData.artists && trackData.artists.length > 0) {
                    const artistNames = trackData.artists
                      .map((artist: any) => artist.name)
                      .filter(Boolean);
                    setArtistName(artistNames.join(", ") || "");
                  }

                  if (trackData.name) {
                    setTitle(trackData.name);
                  }

                  // Set track image URL for preview
                  if (trackData.album?.images && trackData.album.images.length > 0) {
                    // Use medium size image (index 1) or fallback to first image
                    const imageUrl = trackData.album.images[1]?.url || trackData.album.images[0]?.url;
                    setTrackImageUrl(imageUrl || null);
                  } else {
                    setTrackImageUrl(null);
                  }
                }
              } catch (error) {
                console.error("Error fetching track in edit mode:", error);
              } finally {
                setIsFetchingTrack(false);
              }
            }, 100);
          }
        }
      }

      // Countries and interests are now handled directly by the dropdowns via props

      // Pre-fill targeting (age)
      if (adSets && adSets.length > 0) {
        const adSet = adSets[0];
        const targeting = adSet.targeting;

        // Pre-fill targeting (age)
        if (targeting.age_min && targeting.age_max) {
          setAudienceAge(`${targeting.age_min}-${targeting.age_max}`);
        }
      }

      // Pre-fill ad creative from ads
      if (ads && ads.length > 0) {
        const ad = ads[0];
        const creative = ad.creative.object_story_spec;

        // Pre-fill ad text (message)
        if (creative.video_data.message) {
          setAdText(creative.video_data.message);
        }

        // Pre-fill ad headline (title)
        if (creative.video_data.title) {
          setAdHeadline(creative.video_data.title);
        }

        // Set draftAdText for AdvancedSettingsDropdown
        setDraftAdText({
          message: creative.video_data.message || "",
          headlineTop: creative.video_data.title || "",
        });

        // Pre-fill track image URL
        if (creative.video_data.image_url) {
          setTrackImageUrl(creative.video_data.image_url);
        }

        // Pre-fill page ID
        if (creative.page_id) {
          setMetaAdsSelections(prev => ({
            ...prev,
            pageId: creative.page_id,
          }));
        }

        // Extract video IDs from ads to fetch video details
        const videoIds = ads
          .map(ad => ad.creative.object_story_spec?.video_data?.video_id)
          .filter(Boolean);

        // Fetch videos by IDs if we have any
        if (videoIds.length > 0) {
          fetchVideosByIds(videoIds);
        }
      }
    }
  }, [isEditMode, campaignData]);

  // Pre-fill form data when restoring a draft
  useEffect(() => {
    const loadDraftData = async () => {
      if (!isDraftMode || !draftId) return;

      try {
        setIsLoadingDraft(true);
        const token = localStorage.getItem("metaAdsPublicToken");
        const response = await fetch(`${apiBaseUrl}/api/meta-builder/campaigns/draft/${draftId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch draft");
        }

        const result = await response.json();
        const draft = result.draft || result.data; // Support both 'draft' and 'data' keys

  
        // Pre-fill basic fields from draft
        setAdName(draft.campaignName || "");
        setSpotifyUrl(draft.metadata?.spotifyUrl || "");
        setArtistName(draft.metadata?.artistName || "");
        setTitle(draft.metadata?.title || "");

        // Show additional fields if draft has Spotify URL
        if (draft.metadata?.spotifyUrl && draft.metadata.spotifyUrl.trim().length > 0) {
          setShowAdditionalFields(true);
        }

        // Pre-fill budget
        if (draft.budget?.amount !== undefined && draft.budget?.amount !== null) {
          setCampaignBudget(Number(draft.budget.amount).toFixed(2));
        }

        // Pre-fill schedule
        if (draft.schedule?.isScheduled) {
          setIsScheduled('scheduled');
          if (draft.schedule.startDate) {
            setStartDate(dayjs(draft.schedule.startDate));
          }
          if (draft.schedule.endDate) {
            setEndDate(dayjs(draft.schedule.endDate));
          }
          setStartTime(draft.schedule.startTime || "00:00");
          setEndTime(draft.schedule.endTime || "00:00");
        } else {
          setIsScheduled('always');
        }

        // Pre-fill timezone
        if (draft.schedule?.timezone) {
          setTimezone(draft.schedule.timezone);
        }

        // Pre-fill targeting - countries
        if (draft.targeting?.countries && draft.targeting.countries.length > 0) {

          const firstCountry = draft.targeting.countries[0];

          // Check if it's a tier name (e.g., "Tier One Countries")
          const tierMapping: Record<string, string> = {
            "Tier One Countries": "tier-one",
            "Tier Two Countries": "tier-two",
            "Tier Three Countries": "tier-three",
            "Tier One and Two Countries": "tier-one-two",
          };

          if (tierMapping[firstCountry]) {
            // It's a tier name - set it directly
            console.log("Tier name detected, setting:", firstCountry);
            setSelectedCountry(firstCountry);
            setDraftCountries([firstCountry]); // Pass tier name to dropdown
          } else {
            // It's individual country names (rare case - user manually selected specific countries)
            console.log("Individual countries detected");
            const countryString = draft.targeting.countries.join(", ");
            setSelectedCountry(countryString);
            setDraftCountries(draft.targeting.countries);
          }
        }

        // Pre-fill targeting - interests
        if (draft.targeting?.interests && draft.targeting.interests.length > 0) {
          console.log("Setting selectedInterests to:", draft.targeting.interests);
          setSelectedInterests(draft.targeting.interests);
          setDraftInterests(draft.targeting.interests);
        }

        // Pre-fill audience age and gender
        if (draft.targeting?.age_min || draft.targeting?.age_max || draft.targeting?.genders) {
          setDraftTargeting({
            age_min: draft.targeting.age_min,
            age_max: draft.targeting.age_max,
            genders: draft.targeting.genders,
          });

          // Also update the main state for consistency
          if (draft.targeting.age_min && draft.targeting.age_max) {
            const ageRange = `${draft.targeting.age_min}-${draft.targeting.age_max}`;
            setAudienceAge(ageRange);
          }
          if (draft.targeting.genders && draft.targeting.genders.length > 0) {
            const gender = draft.targeting.genders[0] === 1 ? "Male" : "Female";
            setAudienceGender(gender);
          }
        }

        // Pre-fill ad text and headline
        if (draft.adText?.message || draft.adText?.headlineTop) {
          setDraftAdText({
            message: draft.adText.message,
            headlineTop: draft.adText.headlineTop,
          });

          // Also update the main state for consistency
          if (draft.adText?.message) {
            setAdText(draft.adText.message);
          }
          if (draft.adText?.headlineTop) {
            setAdHeadline(draft.adText.headlineTop);
          }
        }

        // Try to get names from sessionStorage first (current active selections)
        let sessionAdAccountName: string | null = null;
        let sessionPageName: string | null = null;
        let sessionInstagramName: string | null = null;
        let sessionPixelName: string | null = null;

        try {
          const raw = sessionStorage.getItem("metaAdsSelections");
          if (raw) {
            const parsed = JSON.parse(raw);
            sessionAdAccountName = parsed?.adAccount?.name || null;
            sessionPageName = parsed?.page?.name || null;
            sessionInstagramName = parsed?.instagram?.name || null;
            sessionPixelName = parsed?.pixel?.name || null;
          }
        } catch (e) {
          console.error("Failed to read sessionStorage for names", e);
        }

        // Update metaAdsSelections with IDs and names
        if (draft.adAccountId || draft.pageId || draft.instagramAccountId || draft.pixelId) {
          setMetaAdsSelections(prev => ({
            ...prev,
            adAccountId: draft.adAccountId || prev.adAccountId,
            pageId: draft.pageId || prev.pageId,
            instagramId: draft.instagramAccountId || prev.instagramId,
            pixelId: draft.pixelId || prev.pixelId,
            // Priority: Metadata names > SessionStorage names > Draft IDs > Previous values
            adAccountName: draft.metadata?.adAccountName || sessionAdAccountName || draft.adAccountId || prev.adAccountName,
            pageName: draft.metadata?.pageName || sessionPageName || draft.pageId || prev.pageName,
            instagramName: draft.metadata?.instagramName || sessionInstagramName || draft.instagramAccountId || prev.instagramName,
            pixelName: draft.metadata?.pixelName || sessionPixelName || draft.pixelId || prev.pixelName,
          }));
        } else {
          console.log("✗ No meta ads data found in draft");
        }

        // Load videos from draft
        if (result.videos && result.videos.length > 0) {
          console.log("=== DRAFT VIDEOS ===");
          console.log("Videos array:", result.videos);

          const draftVideos = result.videos.map((video: any) => ({
            id: video.key || video.url, // Use S3 key or URL as unique ID
            fileName: video.fileName || "Draft Video",
            file: undefined, // No File object for S3 videos
            isUploading: false,
            uploadProgress: 100,
            videoUrl: `${apiBaseUrl}${video.url}`, // Full streaming URL
            thumbnailUrl: undefined, // No thumbnail from draft API
          }));

          setVideos(draftVideos);

          // Auto-select first video if available
          if (draftVideos.length > 0 && !selectedVideoId) {
            setSelectedVideoId(draftVideos[0].id);
          }
        } else {
          console.log("No videos found in draft");
        }

        // Store draft ID for reference (e.g., for updating instead of creating new)
        sessionStorage.setItem("currentDraftId", draft._id);

      } catch (error) {
        console.error("Error loading draft:", error);
      } finally {
        setIsLoadingDraft(false);
      }
    };

    loadDraftData();
  }, [isDraftMode, draftId]);

  // Function to fetch videos by their IDs
  const fetchVideosByIds = async (videoIds: string[]) => {
    try {
      const token = localStorage.getItem("metaAdsPublicToken");
      const response = await fetch(`${apiBaseUrl}/api/meta-builder/videos/by-ids`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ videoIds }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch videos: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Videos data received:", data);

      // Transform the videos data to match the VideoItem structure
      const videoItems = data.videos.map((video: any) => {
        // Get the preferred thumbnail or fallback to first thumbnail
        const preferredThumbnail = video.thumbnails?.data?.find((thumb: any) => thumb.is_preferred);
        const thumbnail = preferredThumbnail || video.thumbnails?.data?.[0];

        return {
          id: video.id,
          fileName: `Video ${video.id}`,
          file: undefined, // No file object since it's from Facebook
          isUploading: false,
          uploadProgress: 100,
          facebookVideoId: video.id,
          videoUrl: video.source, // Video source URL
          thumbnailUrl: thumbnail?.uri || '', // Thumbnail URL
        };
      });

      // Set the videos state with fetched videos
      setVideos(videoItems);
    } catch (error) {
      console.error("Error fetching videos by IDs:", error);
    }
  };

  // Show loading state while fetching campaign data in edit mode
  if (isEditMode && isLoadingCampaign) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-[#58F2A9] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white text-lg">Loading campaign data...</p>
        </div>
      </div>
    );
  }

  // Show loading state while fetching draft data
  if (isDraftMode && isLoadingDraft) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-[#58F2A9] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white text-lg">Loading draft data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen pt-10 pb-10">
      <div className="flex justify-center">
        <div className="w-full flex flex-col gap-4">

          <AdNameNavbar />
          <AdNameStepBar showSecondSection={showSecondSection} />

          {/* Section 1 */}
          {!showSecondSection && (
            <AdNameSection
              onNext={() => setShowSecondSection(true)}
              adName={adName}
              onAdNameChange={setAdName}
              isEditMode={isEditMode}
            />
          )}

          {/* NEW Section 2 (background image only) */}
          {showSecondSection && (
            <section
              className="relative z-[12] w-full max-w-[1308px] mx-auto rounded-[40px] p-[2px]"
              style={{
                background: "linear-gradient(180deg, #58F2A9 0%, #1B76FF 79.33%, rgba(255, 170, 45, 0.1) 95.19%)",
              }}
            >
              <div
                className="relative w-full h-auto rounded-[38px] overflow-hidden bg-[#0B0D12]"
              >
                {/* Background image layer */}
                <div
                  className="absolute inset-0 bg-cover bg-no-repeat bg-center"
                // style={{ backgroundImage: `url(${mabAd2})` }}
                />

                {/* Dark overlay to fully cover the background */}
                <div className="absolute inset-0 bg-[#0B0D12]/80" />

                {/* Content */}
                <div className="relative z-10 w-full h-auto px-6 sm:px-10 md:px-14 lg:px-20 py-14">

                  {/* MAIN WRAPPER */}
                  <div ref={contentContainerRef} className="flex flex-col lg:flex-row gap-10">

                    {/* LEFT CONTENT — DROPDOWNS */}
                    {(() => {
                      // Calculate if preview should be shown (same logic as mobile preview)
                      const isStep1Complete = showSecondSection;
                      const isStep2Complete = spotifyUrl.trim() !== "" && artistName.trim() !== "" && title.trim() !== "";
                      const isStep4Complete = videos.length > 0 && (videos.length > 1 || videos.every(v => !v.isUploading));
                      const shouldShowPreview = isStep1Complete && isStep2Complete && isStep4Complete;

                      return (
                        <div className={`flex-1 w-full lg:max-w-[1100px] order-2 lg:order-1 ${shouldShowPreview ? 'ml-[-10px]' : 'ml-[18px]'}`}>
                          <h2 className="text-white font-bold text-[36px] sm:text-[42px] lg:text-[48px]
                         leading-[100%] mb-8">
                            {isDraftMode ? "Restore Draft" : isEditMode ? "Edit Campaign" : "Grow My Spotify Track"}
                          </h2>

                          <div className="flex flex-col gap-3">
                            {/* Music */}
                            <div ref={(el) => (dropdownRefs.current["music"] = el)}>
                              <MusicDropdown
                                id="music"
                                expandedId={expandedDropdown}
                                onToggle={handleDropdownToggle}
                                onNext={handleNext}
                                spotifyUrl={spotifyUrl}
                                artistName={artistName}
                                title={title}
                                showAdditionalFields={showAdditionalFields}
                                onSpotifyUrlChange={handleSpotifyUrlChange}
                                onArtistNameChange={(e) => setArtistName(e.target.value)}
                                onTitleChange={(e) => setTitle(e.target.value)}
                                isEditMode={isEditMode}
                              />
                            </div>

                            {/* Genre */}
                            {/* <div ref={(el) => (dropdownRefs.current["genre"] = el)}>
              <TrackGenreDropdown
                id="genre"
                expandedId={expandedDropdown}
                onToggle={handleDropdownToggle}
                onNext={handleNext}
                onSelectionChange={setSelectedGenres}
              />
            </div> */}

                            {/* Audio */}
                            {/* <div ref={(el) => (dropdownRefs.current["audio"] = el)}>
              <AudioDropdown
                id="audio"
                expandedId={expandedDropdown}
                onToggle={handleDropdownToggle}
                onNext={handleNext}
                audioFileName={audioFileName}
                onAudioUploadChange={handleAudioUploadChange}
                isAudioUploading={isAudioUploading}
                audioUploadProgress={audioUploadProgress}
                onAudioRemove={handleRemoveAudioUpload}
              />
            </div> */}


                            {/* Landing */}
                            {/* <div ref={(el) => (dropdownRefs.current["landing"] = el)}>
              <LandingDropdown
                id="landing"
                expandedId={expandedDropdown}
                onToggle={handleDropdownToggle}
                onNext={handleNext}
                landingFileName={landingFileName}
                onLandingUploadChange={handleLandingUploadChange}
                isLandingUploading={isLandingUploading}
                landingUploadProgress={landingUploadProgress}
                onLandingRemove={handleRemoveLandingUpload}
              />
            </div> */}
                            {/* Video */}
                            <div ref={(el) => (dropdownRefs.current["video"] = el)}>
                              <VideoDropdown
                                id="video"
                                expandedId={expandedDropdown}
                                onToggle={handleDropdownToggle}
                                onNext={handleNext}
                                videos={videos}
                                onVideoUploadChange={handleVideoUploadChange}
                                onRemoveVideo={handleRemoveVideo}
                                isEditMode={isEditMode}
                                selectedVideoId={selectedVideoId}
                                onVideoSelect={setSelectedVideoId}
                              />
                            </div>


                            {/* Countries */}
                            <div ref={(el) => (dropdownRefs.current["countries"] = el)}>
                              <CountriesDropdown
                                id="countries"
                                expandedId={expandedDropdown}
                                onToggle={handleDropdownToggle}
                                onCountryChange={setSelectedCountry}
                                onNext={handleNext}
                                initialSelectedCountries={
                                  isEditMode
                                    ? (campaignData?.countries || undefined)
                                    : (isDraftMode && draftCountries.length > 0 ? draftCountries : undefined)
                                }
                                isEditMode={isEditMode} // Only disable for edit mode, not draft mode
                              />
                            </div>

                            {/* Interests */}
                            <div ref={(el) => (dropdownRefs.current["interests"] = el)}>
                              <InterestsDropdown
                                id="interests"
                                expandedId={expandedDropdown}
                                onToggle={handleDropdownToggle}
                                onNext={handleNext}
                                onInterestsChange={setSelectedInterests}
                                initialInterests={
                                  isEditMode
                                    ? (() => {
                                        const targeting = campaignData?.adSets?.[0]?.targeting;
                                        console.log("🔍 Edit Mode Interests Debug:", {
                                          hasCampaignData: !!campaignData,
                                          hasAdSets: !!campaignData?.adSets,
                                          adSetsLength: campaignData?.adSets?.length || 0,
                                          hasFirstAdSet: !!campaignData?.adSets?.[0],
                                          hasTargeting: !!targeting,
                                          targeting: targeting,
                                          hasFlexibleSpec: !!targeting?.flexible_spec,
                                          flexibleSpecLength: targeting?.flexible_spec?.length || 0,
                                          hasDirectInterests: !!targeting?.interests,
                                        });

                                        // Try to get interests from flexible_spec first (new API format)
                                        let interests: Array<{ id: string; name: string }> | undefined = undefined;

                                        if (targeting?.flexible_spec && Array.isArray(targeting.flexible_spec)) {
                                          // Combine all interests from all flexible_spec items
                                          const allInterests: Array<{ id: string; name: string }> = [];
                                          targeting.flexible_spec.forEach((spec) => {
                                            if (spec.interests && Array.isArray(spec.interests)) {
                                              allInterests.push(...spec.interests);
                                            }
                                          });

                                          if (allInterests.length > 0) {
                                            interests = allInterests;
                                            console.log("✅ Found interests in flexible_spec:", interests);
                                          }
                                        }

                                        // Fallback to direct interests array (old API format)
                                        if (!interests && targeting?.interests && Array.isArray(targeting.interests) && targeting.interests.length > 0) {
                                          interests = targeting.interests;
                                          console.log("✅ Found interests in direct array:", interests);
                                        }

                                        console.log("📊 Final interests to pass:", interests);
                                        return interests;
                                      })()
                                    : (isDraftMode && draftInterests.length > 0 ? draftInterests : undefined)
                                }
                                isEditMode={isEditMode} // Only disable for edit mode, not draft mode
                              />
                            </div>

                            {/* Budget */}
                            <div ref={(el) => (dropdownRefs.current["budget"] = el)}>
                              <BudgetDropdown
                                id="budget"
                                expandedId={expandedDropdown}
                                onToggle={handleDropdownToggle}
                                onNext={handleNext}
                                onBudgetChange={handleBudgetChange}
                                onCurrencyChange={setCurrency}
                                onStartDateChange={setCampaignStartDate}
                                onEndDateChange={setCampaignEndDate}
                                onScheduleChange={setIsScheduled}
                                onTimezoneChange={setTimezone}
                                onStartDateTimeChange={(date, time) => {
                                  setStartDate(date);
                                  setStartTime(time);
                                }}
                                onEndDateTimeChange={(date, time) => {
                                  setEndDate(date);
                                  setEndTime(time);
                                }}
                                isEditMode={isEditMode}
                                initialBudget={campaignBudget}
                                initialSchedule={isScheduled}
                                initialTimezone={timezone}
                                initialStartDate={startDate}
                                initialEndDate={endDate}
                                initialStartTime={startTime}
                                initialEndTime={endTime}
                                initialCurrency={currency}
                                adAccountId={metaAdsSelections.adAccountId ?? undefined}
                              />
                            </div>

                            {/* Advanced */}
                            <div ref={(el) => (dropdownRefs.current["advanced"] = el)}>
                              <AdvancedSettingsDropdown
                                id="advanced"
                                expandedId={expandedDropdown}
                                onToggle={handleDropdownToggle}
                                onNext={handleNext}
                                onAgeChange={setAudienceAge}
                                onGenderChange={setAudienceGender}
                                onAdTextChange={setAdText}
                                onAdHeadlineChange={setAdHeadline}
                                isEditMode={isEditMode}
                                initialAge={draftTargeting.age_min}
                                initialMaxAge={draftTargeting.age_max}
                                initialGender={draftTargeting.genders?.[0]}
                                initialAdText={draftAdText.message}
                                initialAdHeadline={draftAdText.headlineTop}
                              />
                            </div>

                            {/* Confirmation */}
                            <div ref={(el) => (dropdownRefs.current["confirmation"] = el)}>
                              <ConfirmationDropdown
                                id="confirmation"
                                expandedId={expandedDropdown}
                                onToggle={handleDropdownToggle}
                                isEditMode={isEditMode}
                                campaignId={campaignId}
                              //  onCampaignCreated={handleCampaignCreated}
                                summary={{
                                  adName,
                                  musicUrl: spotifyUrl,
                                  musicArtist: artistName,
                                  musicTitle: title,
                                  genres: selectedGenres,
                                  audioFileName,
                                  isAudioUploading,
                                  audioUploadProgress,
                                  videos: videos.map(v => ({ fileName: v.fileName, isUploading: v.isUploading, uploadProgress: v.uploadProgress, file: v.file, videoUrl: v.videoUrl })),
                                  landingFileName,
                                  isLandingUploading,
                                  landingUploadProgress,
                                  country: selectedCountry,
                                  interests: selectedInterests,
                                  adAccountName: metaAdsSelections.adAccountName ?? undefined,
                                  pageName: metaAdsSelections.pageName ?? undefined,
                                  instagramName: metaAdsSelections.instagramName ?? undefined,
                                  pixelName: metaAdsSelections.pixelName ?? undefined,
                                  audienceAge,
                                  audienceGender,
                                  campaignBudget,
                                  currency,
                                  campaignStartDate,
                                  campaignEndDate,
                                  trackImageUrl,
                                  adAccountId: metaAdsSelections.adAccountId ?? undefined,
                                  pageId: metaAdsSelections.pageId ?? undefined,
                                  instagramId: metaAdsSelections.instagramId ?? undefined,
                                  pixelId: metaAdsSelections.pixelId ?? undefined,
                                  isScheduled,
                                  timezone,
                                  startDate,
                                  startTime,
                                  endDate,
                                  endTime,
                                  adText,
                                  adHeadline,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* RIGHT — MOBILE PREVIEW */}
                    {/* Show preview only after Step 1, 2 are complete AND video is uploaded in Step 4 */}
                    {(() => {
                      const isStep1Complete = showSecondSection; // Ad Name section completed
                      const isStep2Complete = spotifyUrl.trim() !== "" && artistName.trim() !== "" && title.trim() !== ""; // Music section completed
                      const isStep4Complete = videos.length > 0 && (videos.length > 1 || videos.every(v => !v.isUploading)); // At least one video uploaded, and if only 1 video then it must be complete

                      const shouldShowPreview = isStep1Complete && isStep2Complete && isStep4Complete;

                      return shouldShowPreview ? (
                        <div
                          ref={previewElementRef}
                          className="w-full lg:w-[450px] xl:w-[500px] order-1 lg:order-2 flex flex-col items-center mt-[65px] ml-[-75px] mr-[-75px]
                          flex-shrink-0 self-start will-change-transform"
                          style={{
                            transform: `translateY(${previewOffset}px)`,
                          }}
                        >
                          <MobileAdPreview
                            showPreview
                            spotifyUrl={spotifyUrl}
                            videoFile={videos.length > 0 ? videos[0].file : null}
                            videos={videos}
                            selectedVideoId={selectedVideoId}
                            onVideoSelect={setSelectedVideoId}
                            audioFile={audioFile}
                            trackImageUrl={trackImageUrl}
                            landingImageFile={landingFile}
                            title={title}
                            artistName={artistName}
                            adText={adText}
                            adHeadline={adHeadline}
                            instagramName={metaAdsSelections.instagramName}
                            pageName={metaAdsSelections.pageName}
                          />
                        </div>
                      ) : null;
                    })()}

                  </div>
                </div>
              </div>
            </section>
          )}

        </div>
      </div>
    </div>
  );
}
